if (!window.EsimPredictiveSearch) {
const SEARCH_ALIAS_DEFINITIONS = [
    {
        aliases: ['usa', 'us', 'america', 'united states of america'],
        label: 'United States'
    },
    {
        aliases: ['uk', 'u.k.', 'britain', 'great britain', 'england'],
        label: 'United Kingdom'
    },
    {
        aliases: ['uae', 'u.a.e.', 'dubai', 'abu dhabi'],
        label: 'United Arab Emirates'
    },
    {
        aliases: ['ksa', 'saudi', 'saudi arabia'],
        label: 'Saudi Arabia'
    },
    {
        aliases: ['jp', 'japan'],
        label: 'Japan'
    },
    {
        aliases: ['korea', 'south korea', 'republic of korea'],
        label: 'South Korea'
    }
];

function normalizeAliasValue(value) {
    const spaced = String(value || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim()
        .replace(/\s+/g, ' ');
    const parts = spaced ? spaced.split(' ') : [];

    return parts.length > 1 && parts.every((part) => part.length === 1)
        ? parts.join('')
        : spaced;
}

const SEARCH_ALIAS_LOOKUP = SEARCH_ALIAS_DEFINITIONS.reduce((lookup, definition) => {
    const normalizedDefinition = {
        ...definition,
        targetKey: normalizeAliasValue(definition.label)
    };

    definition.aliases.forEach((alias) => {
        lookup[normalizeAliasValue(alias)] = normalizedDefinition;
    });

    return lookup;
}, {});

class EsimPredictiveSearch {
    constructor(form, input, resultsId, options = {}) {
        this.form = form;
        this.input = input;
        this.results = document.getElementById(resultsId);
        this.options = {
            searchUrl: '/search',
            currency: window.shopCurrency || '$',
            limit: 20,
            ...options
        };
        this.currentQuery = '';
        this.cache = {};

        this.init();
    }

    init() {
        if (!this.form || !this.input || !this.results) return;

        // Prevent propagation for inputs to avoid external handlers
        ['input', 'keyup', 'keydown', 'change', 'focus'].forEach((evt) => {
            this.input.addEventListener(evt, (e) => e.stopPropagation());
        });

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            window.location.href = `${this.options.searchUrl}?q=${encodeURIComponent(this.input.value)}&type=product`;
        });

        this.input.addEventListener('input', this.debounce(this.onChange.bind(this), 250));

        // Show cached results on focus
        this.input.addEventListener('focus', () => {
            if (this.currentQuery.length > 0) this.open();
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
            if (!this.form.contains(e.target)) this.close();
        });
    }

    debounce(func, wait) {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    onChange(e) {
        if (e && e.stopPropagation) e.stopPropagation();
        const query = this.input.value.trim();
        if (query === this.currentQuery) return;
        this.currentQuery = query;

        if (query.length === 0) {
            this.close();
            return;
        }

        if (this.cache[query]) {
            this.render(this.cache[query]);
        } else {
            this.fetch(query);
        }
    }

    buildSuggestUrl(query) {
        const endpoint = `${this.options.searchUrl}/suggest.json`;
        const params = new URLSearchParams({
            'resources[type]': 'product',
            'resources[options][fields]': 'title,vendor,product_type,tag,body',
            'resources[limit]': this.options.limit,
            'q': query
        });

        return `${endpoint}?${params.toString()}`;
    }

    fetch(query) {
        const aliasConfig = this.getAliasConfig(query);
        const requestQueries = [query];

        if (aliasConfig && this.normalizeAliasQuery(aliasConfig.label) !== this.normalizeAliasQuery(query)) {
            requestQueries.push(aliasConfig.label);
        }

        Promise.allSettled(requestQueries.map((requestQuery) => (
            fetch(this.buildSuggestUrl(requestQuery)).then((r) => r.json())
        )))
            .then((responses) => {
                if (query !== this.currentQuery) return;

                const rejectedResponses = responses.filter((response) => response.status === 'rejected');
                rejectedResponses.forEach((response) => console.error('Search Error:', response.reason));

                const fulfilledResponses = responses
                    .filter((response) => response.status === 'fulfilled')
                    .map((response) => response.value);
                if (fulfilledResponses.length === 0) {
                    this.close();
                    return;
                }

                const data = fulfilledResponses.reduce((mergedData, response) => this.mergeSearchData(mergedData, response));
                this.cache[query] = data;
                this.render(data);
            })
            .catch((e) => console.error('Search Error:', e));
    }

    mergeSearchData(primaryData, additionalData) {
        if (!primaryData) return additionalData;
        if (!additionalData) return primaryData;

        const primaryProducts = primaryData.resources?.results?.products || [];
        const additionalProducts = additionalData.resources?.results?.products || [];
        const seenUrls = new Set(primaryProducts.map((product) => this.getProductIdentity(product)));
        const mergedProducts = [...primaryProducts];

        additionalProducts.forEach((product) => {
            const identity = this.getProductIdentity(product);
            if (seenUrls.has(identity)) return;

            seenUrls.add(identity);
            mergedProducts.push(product);
        });

        return {
            ...primaryData,
            resources: {
                ...(primaryData.resources || {}),
                results: {
                    ...(primaryData.resources?.results || {}),
                    products: mergedProducts
                }
            }
        };
    }

    open() {
        this.results.classList.add('active');
        this.results.setAttribute('aria-expanded', 'true');
    }

    close() {
        this.results.classList.remove('active');
        this.results.setAttribute('aria-expanded', 'false');
    }

    render(data) {
        const results = data.resources.results.products || [];
        let html = '';

        if (results.length > 0) {
            const regularProds = [];
            const multiProds = [];

            results.forEach((p) => {
                if (p.tags && p.tags.includes('multicountry')) {
                    multiProds.push(p);
                } else {
                    regularProds.push(p);
                }
            });

            html += `<div class="results-body">`;

            // 1. Regular/Single Country Plans
            if (regularProds.length > 0) {
                html += `<div class="section-label">Top matches</div>`;
                const grouped = this.group(regularProds);
                const aliasConfig = this.getAliasConfig(this.currentQuery);
                const groupedEntries = this.getRankedGroupEntries(grouped, aliasConfig);

                for (const [key, items] of groupedEntries) {
                    // Sort by price
                    items.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

                    const primaryProduct = this.pickPrimaryProduct(items);

                    const displayName = this.getCleanName(primaryProduct.title);
                    const flagUrl = primaryProduct.image || items[0].image || '';
                    const lowestPrice = this.formatPrice(items[0].price);
                    const productUrl = primaryProduct.url || items[0].url;

                    html += `
          <a href="${productUrl}" class="country-row">
            ${flagUrl
                            ? `<img src="${flagUrl.replace(/(\.jpg|\.png)/, '_64x64_crop_center$1')}" class="country-flag" alt="${displayName}" width="28" height="28" loading="lazy">`
                            : ''
                        }
            <span class="country-name">${displayName}</span>
            <span class="country-price">from ${lowestPrice}</span>
          </a>`;
                }
            }

            // 2. Multi-Country/Regional Plans
            if (multiProds.length > 0) {
                multiProds.sort((a, b) => parseFloat(a.price) - parseFloat(b.price));

                html += `
        <div class="section-label" style="margin-top: 0;">Going bigger?</div>
        <div class="multicountry-header">
          <span class="country-flag icon-global">🌐</span>
          <span class="country-name">Regional and global plans</span>
        </div>`;

                multiProds.forEach((item) => {
                    const cleanTitle = item.title.replace(/\s*(esim|data|plan)+$/gi, '').trim();
                    const price = this.formatPrice(item.price);

                    html += `
          <a href="${item.url}" class="country-row multicountry-row">
            <span class="country-name">${cleanTitle}</span>
            <span class="country-price">from ${price}</span>
          </a>`;
                });
            }

            html += `</div>`; // End results-body
        } else {
            html = `<div class="no-results">No luck for "${this.currentQuery}" yet. Try a country, region, or something broader like "Europe".</div>`;
        }

        // View All Link
        html += `
      <div class="search-footer">
        <a href="${this.options.searchUrl}?q=${encodeURIComponent(this.currentQuery)}" class="view-all-link">
          See every plan for "${this.currentQuery}" &rarr;
        </a>
      </div>
    `;

        this.results.innerHTML = html;
        this.open();
    }

    formatPrice(price) {
        if (!isNaN(parseFloat(price)) && !/[^0-9.,\s]/.test(price)) {
            return this.options.currency + parseFloat(price).toFixed(2);
        }
        return price;
    }

    getCleanName(title) {
        let name = (title || '').split(/ [-|] /)[0].split(' (')[0].trim();
        const suffixPattern = /\s+(daily\s+data\s+plan|daily\s+esim|day\s*pass|unlimited|standard|basic|premium|esim|data\s+plan|data|plan|gb)$/i;

        while (suffixPattern.test(name)) {
            name = name.replace(suffixPattern, '').trim();
        }

        return name;
    }

    getProductIdentity(product) {
        const handle = this.getHandle(product);
        if (handle) return handle;

        const url = product && product.url ? product.url : '';
        return url.split('?')[0].split('#')[0].toLowerCase() || JSON.stringify(product || {});
    }

    getHandle(product) {
        const productUrl = product && product.url ? product.url : '';
        const handleMatch = productUrl.match(/\/products\/([^/?#]+)/i);
        return handleMatch ? handleMatch[1].toLowerCase() : '';
    }

    getGroupKey(product) {
        const handle = this.getHandle(product);
        if (handle) {
            return handle.replace(/(?:_|-)(package|unlimited|zone|daypass)$/i, '');
        }

        return this.getCleanName(product && product.title).toLowerCase();
    }

    normalizeAliasQuery(value) {
        return normalizeAliasValue(value);
    }

    getAliasConfig(query) {
        return SEARCH_ALIAS_LOOKUP[this.normalizeAliasQuery(query)] || null;
    }

    productMatchesAliasTarget(product, aliasConfig) {
        return this.normalizeAliasQuery(this.getCleanName(product && product.title)) === aliasConfig.targetKey;
    }

    getAliasRank(products, aliasConfig) {
        if (!aliasConfig) return 1;

        return products.some((product) => this.productMatchesAliasTarget(product, aliasConfig))
            ? 0
            : 1;
    }

    getRankedGroupEntries(grouped, aliasConfig) {
        const entries = Object.entries(grouped);
        if (!aliasConfig) return entries;

        return entries
            .map((entry, index) => ({
                entry,
                index,
                rank: this.getAliasRank(entry[1], aliasConfig)
            }))
            .sort((left, right) => {
                if (left.rank !== right.rank) {
                    return left.rank - right.rank;
                }

                return left.index - right.index;
            })
            .map(({ entry }) => entry);
    }

    pickPrimaryProduct(products) {
        const rankedProducts = [...products].sort((left, right) => {
            const leftHandle = this.getHandle(left);
            const rightHandle = this.getHandle(right);
            const leftPriority = /(?:_|-)(package|zone)$/i.test(leftHandle)
                ? 0
                : /(?:_|-)(unlimited|daypass)$/i.test(leftHandle)
                    ? 1
                    : 2;
            const rightPriority = /(?:_|-)(package|zone)$/i.test(rightHandle)
                ? 0
                : /(?:_|-)(unlimited|daypass)$/i.test(rightHandle)
                    ? 1
                    : 2;

            if (leftPriority !== rightPriority) {
                return leftPriority - rightPriority;
            }

            return (left.title || '').length - (right.title || '').length;
        });

        return rankedProducts[0];
    }

    group(products) {
        const g = {};
        products.forEach((p) => {
            const key = this.getGroupKey(p);
            if (!g[key]) g[key] = [];
            g[key].push(p);
        });
        return g;
    }
}

// Attach to window
window.EsimPredictiveSearch = EsimPredictiveSearch;
}
